import { useEffect, useMemo, useState } from "react";

import { cn } from "@/lib/utils";
import type { Province } from "../lib/regions.data.ts";

/**
 * ═══════════════════════════════════════════════════════════════
 *  省 / 市 / 区三级联动
 * ═══════════════════════════════════════════════════════════════
 *
 * 以前这三格是**手打的文本框**。手打有两个后果，都要到发货那天才发现：
 *
 * 🔴 **错别字没人拦。** 「浙江」「浙江省」「淅江省」在库里是三个不同的值，
 *    打单、算运费、按地区统计全都对不上，而下单当时一切正常。
 * 🔴 **建材是大件配送，地址错一次就是一趟车白跑。** 让用户在工地上单手
 *    打三级地名，是把最容易出错的一步交给了最不方便的场景。
 *
 * ⚠️ 用原生 `<select>` 而不是自己画下拉：手机上原生控件是系统滚轮选择器，
 *    单手能用、无障碍也是现成的；自己画的浮层在小屏上永远差一口气。
 */

interface RegionValue {
  province: string;
  city: string;
  district: string;
}

/**
 * 🔴 **数据要动态 import，不能静态引。**
 *    省市区表有五十多 KB，而全站只有"填收货地址"这一处用得上 ——
 *    静态引的话每个打开首页的人都得为它多下一段。
 *
 * ⚠️ 模块级缓存：同一次会话里打开第二个地址表单不该再下一遍。
 */
let cached: Province[] | null = null;
let loading: Promise<Province[]> | null = null;

function loadRegions(): Promise<Province[]> {
  if (cached) return Promise.resolve(cached);
  loading ??= import("../lib/regions.data.ts").then((m) => {
    cached = m.REGIONS;
    return cached;
  });
  return loading;
}

/**
 * ⚠️ 库里存着的值可能**不在这张表里**（老数据、港澳台、区划调整过）。
 *    这时候要把它当成一个额外的选项塞进去 —— 直接不认的话，
 *    用户只是点进来改个手机号，省市区就被悄悄清空了。
 */
function withCurrent(options: readonly string[], current: string): string[] {
  if (!current || options.includes(current)) return [...options];
  return [current, ...options];
}

export function RegionPicker({
  value,
  onChange,
  withLabels = false,
}: {
  value: RegionValue;
  onChange: (next: RegionValue) => void;
  /** 网页版的表单每格上面带一行小标题，手机版不带。 */
  withLabels?: boolean;
}) {
  const [regions, setRegions] = useState<Province[] | null>(cached);

  useEffect(() => {
    if (cached) return;
    let alive = true;
    void loadRegions().then((r) => {
      if (alive) setRegions(r);
    });
    return () => {
      alive = false;
    };
  }, []);

  const provinces = useMemo(() => (regions ?? []).map((p) => p[0]), [regions]);
  const cities = useMemo(
    () => regions?.find((p) => p[0] === value.province)?.[1].map((c) => c[0]) ?? [],
    [regions, value.province],
  );
  const districts = useMemo(
    () =>
      regions
        ?.find((p) => p[0] === value.province)?.[1]
        .find((c) => c[0] === value.city)?.[1] ?? [],
    [regions, value.province, value.city],
  );

  const pickProvince = (province: string) => {
    const cs = regions?.find((p) => p[0] === province)?.[1] ?? [];
    /**
     * ⚠️ 只有一个市级的（直辖市的「市辖区」、海南的省直辖县）自动选上 ——
     *    让用户在一个只有一个选项的下拉框里点一下，纯粹是多一步。
     */
    const city = cs.length === 1 ? cs[0]![0] : "";
    onChange({ province, city, district: "" });
  };

  const cls = (enabled: boolean) =>
    cn(
      "h-10 w-full rounded-lg border border-border bg-background px-2.5 text-[16px] outline-none focus:border-brand-500",
      !enabled && "text-muted-foreground",
    );

  /** ⚠️ 数据还没到时不要让人点开一个空下拉 —— 先禁用，一两百毫秒的事。 */
  const ready = regions !== null;

  return (
    <>
      <Field label={withLabels ? "省" : undefined}>
        <select
          className={cls(ready)}
          value={value.province}
          disabled={!ready}
          onChange={(e) => pickProvince(e.target.value)}
        >
          <option value="">{ready ? "省 / 直辖市" : "加载中…"}</option>
          {withCurrent(provinces, value.province).map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
      </Field>

      <Field label={withLabels ? "市" : undefined}>
        <select
          className={cls(Boolean(value.province))}
          value={value.city}
          disabled={!ready || !value.province}
          onChange={(e) => onChange({ ...value, city: e.target.value, district: "" })}
        >
          <option value="">市</option>
          {withCurrent(cities, value.city).map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </Field>

      <Field label={withLabels ? "区" : undefined}>
        <select
          className={cls(Boolean(value.city))}
          value={value.district}
          disabled={!ready || !value.city}
          onChange={(e) => onChange({ ...value, district: e.target.value })}
        >
          <option value="">区 / 县</option>
          {withCurrent(districts, value.district).map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </select>
      </Field>
    </>
  );
}

function Field({ label, children }: { label?: string; children: React.ReactNode }) {
  if (!label) return <>{children}</>;
  return (
    <label className="block">
      <span className="mb-1.5 block text-[13px] text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}
